import { Link } from 'react-router-dom'
import { useTheme } from '../contexts/ThemeContext'

const Services = () => {
  const { isNight } = useTheme()

  const services = [
    {
      title: 'External Network Penetration Test',
      description: 'Comprehensive assessment of your internet-facing infrastructure to identify vulnerabilities that could be exploited by external attackers.',
      icon: (
        <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
        </svg>
      ),
      features: [
        'Perimeter security testing',
        'External firewall assessment',
        'Open port and service enumeration',
        'Exploitation of identified vulnerabilities',
        'Detailed findings and remediation report',
      ],
      why: 'Your external network is the first line of defense. Understanding how attackers view your infrastructure from the outside is critical to preventing breaches.'
    },
    {
      title: 'External Web Application Penetration Test',
      description: 'In-depth security testing of your web applications, member portals, and APIs to uncover vulnerabilities before malicious actors do.',
      icon: (
        <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
        </svg>
      ),
      features: [
        'OWASP Top 10 vulnerability testing',
        'Authentication and authorization testing',
        'Session management analysis',
        'Input validation and injection testing',
        'API security assessment',
      ],
      why: 'Web applications are frequently targeted by attackers. Thorough testing ensures your applications are secure and your data is protected.'
    },
    {
      title: 'External OSINT Assessment',
      description: 'Open Source Intelligence gathering to identify what information about your organization is publicly available and how it could be used against you.',
      icon: (
        <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      ),
      features: [
        'Public data reconnaissance',
        'Social media footprint analysis',
        'Exposed credential identification',
        'Data leak detection',
        'Security awareness recommendations',
      ],
      why: 'Attackers use publicly available information to plan their attacks. Understanding your digital footprint helps you minimize exposure and risk.'
    },
    {
      title: 'Internal Network Security Controls Assessment',
      description: 'Comprehensive evaluation of your internal network security posture, testing defenses as if an attacker had gained initial access.',
      icon: (
        <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
      features: [
        'Internal network architecture review',
        'Privilege escalation testing',
        'Lateral movement assessment',
        'Active Directory security evaluation',
        'Internal segmentation effectiveness',
      ],
      why: 'Most breaches involve lateral movement within networks. Testing internal controls ensures you can detect and contain threats before critical systems are compromised.'
    },
    {
      title: 'Internal Vulnerability Assessment',
      description: 'Systematic scanning and analysis of your internal systems to identify, classify, and prioritize security vulnerabilities.',
      icon: (
        <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      features: [
        'Comprehensive vulnerability scanning',
        'Asset inventory and classification',
        'Risk-based prioritization',
        'Patch management guidance',
        'Trend analysis and metrics',
      ],
      why: 'Regular vulnerability assessments are essential for maintaining security hygiene and meeting compliance requirements. Catch vulnerabilities before they become breaches.'
    },
  ]

  return (
    <div className={isNight ? 'bg-void' : 'bg-gradient-to-b from-white to-gray-50'}>
      {/* Header */}
      <div className={`py-16 ${isNight ? 'bg-gradient-to-r from-void-light/80 to-void-elevated/80 backdrop-blur-sm' : 'glass-hero text-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className={`text-4xl md:text-5xl font-bold mb-4 ${isNight ? 'font-orbitron text-cyber-cyan' : ''}`}>
            {isNight ? 'OUR SERVICES' : 'Our Services'}
          </h1>
          <p className={isNight ? 'text-xl text-gray-400' : 'text-xl text-primary-100'}>
            {isNight ? 'Comprehensive threat assessment and vulnerability analysis' : 'Comprehensive security assessment services tailored to your needs'}
          </p>
        </div>
      </div>

      {/* Services Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              className={`rounded-xl p-6 transition-all duration-300 group ${
                isNight
                  ? 'neon-border-gradient'
                  : 'card'
              }`}
            >
              <div className="flex items-start">
                <div className={`mr-4 ${isNight ? 'text-cyber-cyan' : 'text-primary-600'}`}>
                  {service.icon}
                </div>
                <div className="flex-1">
                  <h3 className={`text-2xl font-bold mb-3 ${isNight ? 'text-white' : 'text-gray-900'}`}>
                    {service.title}
                  </h3>
                  <p className={`mb-4 ${isNight ? 'text-gray-400' : 'text-gray-600'}`}>
                    {service.description}
                  </p>

                  <div className="mb-4">
                    <h4 className={`font-semibold mb-2 ${isNight ? 'text-cyber-cyan' : 'text-primary-600'}`}>
                      What we provide:
                    </h4>
                    <ul className={`list-disc list-inside space-y-1 ${isNight ? 'text-gray-400' : 'text-gray-600'}`}>
                      {service.features.map((feature, idx) => (
                        <li key={idx}>{feature}</li>
                      ))}
                    </ul>
                  </div>

                  <div className={`p-4 rounded-lg backdrop-blur-sm ${isNight ? 'bg-void-elevated/50 border border-cyber-magenta/20' : 'bg-primary-50/70 border border-primary-100/50'}`}>
                    <h4 className={`font-semibold mb-2 ${isNight ? 'text-cyber-magenta' : 'text-primary-700'}`}>
                      Why you need it:
                    </h4>
                    <p className={`text-sm ${isNight ? 'text-gray-400' : 'text-gray-700'}`}>
                      {service.why}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-16">
          <div className={`text-center max-w-2xl mx-auto ${isNight ? 'neon-border-gradient p-8' : 'card'}`}>
            <h2 className={`text-3xl font-bold mb-6 ${isNight ? 'text-white font-orbitron' : 'text-gray-900'}`}>
              {isNight ? 'NEED A CUSTOM SOLUTION?' : 'Need a Custom Solution?'}
            </h2>
            <p className={`text-xl mb-8 ${isNight ? 'text-gray-400' : 'text-gray-600'}`}>
              {isNight ? 'We can tailor our services to meet your specific security requirements.' : 'We can tailor our services to meet your specific security requirements.'}
            </p>
            <Link to="/contact" className={isNight ? 'btn-neon backdrop-blur-xl' : 'btn-primary'}>
              {isNight ? 'Contact Us' : 'Contact Us Today'}
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Services
